import React, { useLayoutEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { gsap } from "gsap";
import { Container, Eyebrow, Display, Lead, useReveal } from "./ui";
import { colors, fonts, media, copperGradient, prefersReducedMotion } from "../../styles/theme";
import { roadmap } from "../../pages/landingContent";

const NODE_GAP = 100 / (roadmap.stages.length - 1);

const pulse = keyframes`
  0% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
  100% { transform: translate(-50%, -50%) scale(3.2); opacity: 0; }
`;

const march = keyframes`
  to { background-position: 28px 0; }
`;

const Wrap = styled.section`
  padding: 140px 0;
  background: ${colors.sand};
  overflow: hidden;
`;

const Head = styled.div`
  display: grid;
  gap: 24px;
  margin-bottom: 72px;

  @media ${media.medium} {
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
    align-items: end;
  }
`;

// Full-bleed scroller so the line runs edge to edge; scrolls sideways on small screens.
const Scroller = styled.div`
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Track = styled.div`
  position: relative;
  --pad: max(80px, calc((100vw - 1200px) / 2));
  min-width: 1120px;
  padding: 0 var(--pad);
  height: 200px;
`;

const Rail = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 138px;
  height: 2px;
  background-image: linear-gradient(90deg, ${colors.pebble} 50%, transparent 0);
  background-size: 14px 2px;
  opacity: 0.45;
`;

const Inner = styled.div`
  position: relative;
  height: 100%;
`;

const Fill = styled.div`
  position: absolute;
  left: calc(-1 * var(--pad));
  top: 136px;
  height: 6px;
  border-radius: 6px;
  background: ${copperGradient};
  width: ${(p) => `calc(${p.$to}% + var(--pad))`};
  transform-origin: 0 50%;
  box-shadow: 0 6px 20px -6px rgba(143, 83, 41, 0.7);
`;

const Building = styled.div`
  position: absolute;
  top: 137px;
  left: ${(p) => p.$from}%;
  width: ${NODE_GAP}%;
  height: 4px;
  border-radius: 4px;
  background-image: repeating-linear-gradient(90deg, ${colors.copperGlow} 0 14px, transparent 14px 28px);
  mask-image: linear-gradient(90deg, #000, transparent);
  -webkit-mask-image: linear-gradient(90deg, #000, transparent);
  animation: ${march} 1.2s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Node = styled.button`
  position: absolute;
  top: 0;
  height: 100%;
  width: 196px;
  transform: translateX(-50%);
  background: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: ${fonts.body};

  .card {
    position: relative;
    width: 100%;
    height: 108px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 14px;
    border-radius: 16px;
    text-align: left;
    border: 1px solid ${(p) => (p.$selected ? colors.forest : colors.stone)};
    background: ${(p) => (p.$selected ? colors.forest : colors.linen)};
    box-shadow: ${(p) => (p.$selected ? "0 18px 36px -20px rgba(30, 42, 34, 0.6)" : "0 6px 16px -12px rgba(42, 35, 30, 0.35)")};
    transition: background 0.3s ease, border-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
  }
  .card::after {
    content: "";
    position: absolute;
    left: 50%;
    bottom: -21px;
    width: 1px;
    height: 20px;
    background: ${(p) => (p.$selected ? colors.forest : colors.stone)};
  }
  &:hover .card {
    transform: translateY(-3px);
    border-color: ${colors.forest};
  }
  .label {
    font-family: ${fonts.mono};
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${(p) => (p.$selected ? colors.copperLight : p.$done ? colors.copper : colors.pebble)};
  }
  .name {
    font-family: ${fonts.display};
    font-size: 17px;
    line-height: 1.15;
    letter-spacing: -0.01em;
    color: ${(p) => (p.$selected ? colors.linen : colors.ink)};
    transition: color 0.3s ease;
  }
  .cue {
    margin-top: auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: ${fonts.body};
    font-size: 12px;
    font-weight: 600;
    color: ${(p) => (p.$selected ? colors.sage : colors.copper)};
  }
  .dot {
    position: absolute;
    top: 139px;
    left: 50%;
    width: ${(p) => (p.$current ? "22px" : "16px")};
    height: ${(p) => (p.$current ? "22px" : "16px")};
    transform: translate(-50%, -50%);
    border-radius: 50%;
    background: ${(p) => (p.$done ? colors.copper : colors.sand)};
    border: 2px solid ${(p) => (p.$done ? colors.copperLight : colors.pebble)};
    box-shadow: ${(p) => (p.$selected ? `0 0 0 6px rgba(143, 83, 41, 0.18)` : "none")};
    transition: box-shadow 0.3s ease, transform 0.3s ease;
  }
  &:hover .dot {
    transform: translate(-50%, -50%) scale(1.2);
  }
  .here {
    position: absolute;
    top: 168px;
    padding: 6px 12px;
    border-radius: 999px;
    background: ${colors.ink};
    color: ${colors.linen};
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
  }
  .here::before {
    content: "";
    position: absolute;
    left: 50%;
    top: -5px;
    width: 10px;
    height: 10px;
    background: ${colors.ink};
    transform: translateX(-50%) rotate(45deg);
  }
  .when {
    position: absolute;
    top: 170px;
    font-size: 13px;
    color: ${colors.pebble};
  }
`;

const Ping = styled.span`
  position: absolute;
  top: 139px;
  left: 50%;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${colors.copperGlow};
  animation: ${pulse} 1.8s ease-out infinite;
  pointer-events: none;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0;
  }
`;

const Detail = styled.div`
  margin-top: 32px;
  display: grid;
  gap: 24px;
  padding: 32px;
  border-radius: 28px;
  background: ${colors.linen};
  border: 1px solid ${colors.stone};

  @media ${media.medium} {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    padding: 44px;
    gap: 48px;
    align-items: center;
  }
`;

const Hint = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: -36px 0 20px;
  padding: 8px 14px;
  border-radius: 999px;
  background: ${colors.linen};
  border: 1px solid ${colors.stone};
  font-family: ${fonts.mono};
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${colors.bark};

  span {
    color: ${colors.copper};
  }
`;

const DetailTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Stepper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${fonts.mono};
  font-size: 12px;
  color: ${colors.pebble};

  button {
    display: grid;
    place-items: center;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    border: 1px solid ${colors.stone};
    background: ${colors.limestone};
    color: ${colors.ink};
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease;
  }
  button:hover:not(:disabled) {
    border-color: ${colors.ink};
    background: ${colors.sand};
  }
  button:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

const Status = styled.span`
  display: inline-flex;
  padding: 6px 12px;
  border-radius: 999px;
  font-family: ${fonts.mono};
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${(p) => (p.$now ? colors.linen : colors.bark)};
  background: ${(p) => (p.$now ? colors.moss : colors.sand)};
`;

const DetailTitle = styled.h3`
  margin: 16px 0 12px;
  font-family: ${fonts.display};
  font-weight: 300;
  font-size: clamp(32px, 3.4vw, 48px);
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: ${colors.ink};
`;

const Items = styled.ul`
  list-style: none;
  display: grid;
  gap: 10px;

  li {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 18px;
    border-radius: 14px;
    background: ${colors.limestone};
    font-family: ${fonts.body};
    font-size: 16px;
    color: ${colors.ink};
  }
  li::before {
    content: "";
    flex: none;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(p) => (p.$now ? colors.moss : colors.copperGlow)};
  }
`;

const statusFor = (i) => {
  if (i < roadmap.currentIndex) return "Complete";
  if (i === roadmap.currentIndex) return "Now · working product";
  const when = roadmap.stages[i].when;
  if (i === roadmap.currentIndex + 1) return `Next · ${when || "in development"}`;
  return when ? `Planned · ${when}` : "Later";
};

const Roadmap = () => {
  const scope = useRef(null);
  const [selected, setSelected] = useState(roadmap.currentIndex);
  const current = roadmap.currentIndex;
  const stage = roadmap.stages[selected];
  useReveal(scope);

  useLayoutEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ scrollTrigger: { trigger: "[data-track]", start: "top 80%", once: true } });
      tl.from("[data-fill]", { scaleX: 0, duration: 1.4, ease: "power3.inOut" })
        .from("[data-node]", { y: 16, opacity: 0, duration: 0.6, stagger: 0.08, ease: "power3.out" }, 0.2)
        .from("[data-rail]", { scaleX: 0, transformOrigin: "0 50%", duration: 1.6, ease: "power2.out" }, 0);
    }, scope);
    return () => ctx.revert();
  }, []);

  return (
    <Wrap id="roadmap" ref={scope} aria-labelledby="roadmap-title">
      <Container>
        <Head>
          <div data-reveal>
            <Eyebrow $color={colors.copper}>{roadmap.eyebrow}</Eyebrow>
            <Display id="roadmap-title" style={{ marginTop: 20 }}>
              {roadmap.titleLead} <em>{roadmap.titleEmphasis}</em>
            </Display>
          </div>
          <Lead data-reveal="0.1">{roadmap.body}</Lead>
        </Head>
      </Container>

      <Container>
        <Hint data-reveal>
          <span aria-hidden="true">↓</span> {roadmap.hint}
        </Hint>
      </Container>
      <Scroller>
        <Track data-track>
          <Rail data-rail />
          <Inner>
            <Fill data-fill $to={current * NODE_GAP} />
            <Building $from={current * NODE_GAP} />
            {roadmap.stages.map((s, i) => (
              <Node
                key={s.short}
                type="button"
                data-node
                style={{ left: `${i * NODE_GAP}%` }}
                $done={i <= current}
                $current={i === current}
                $selected={i === selected}
                aria-pressed={i === selected}
                aria-label={`${s.short}, ${s.when}: ${s.title}. ${statusFor(i)}. Show details`}
                onClick={() => setSelected(i)}
              >
                <span className="card">
                  <span className="label">
                    {s.short} · {s.when}
                  </span>
                  <span className="name">{s.nodeName || s.title}</span>
                  <span className="cue">{i === selected ? "Showing details ↓" : "View details →"}</span>
                </span>
                {i === current && <Ping aria-hidden="true" />}
                <span className="dot" aria-hidden="true" />
                {i === current && <span className="here">We are here</span>}
                {i === current + 1 && <span className="when">Up next</span>}
              </Node>
            ))}
          </Inner>
        </Track>
      </Scroller>

      <Container>
        <Detail aria-live="polite">
          <div>
            <DetailTop>
              <Status $now={selected === current}>{statusFor(selected)}</Status>
              <Stepper>
                <button type="button" aria-label="Previous stage" disabled={selected === 0} onClick={() => setSelected((v) => Math.max(0, v - 1))}>
                  ←
                </button>
                <span>
                  {selected + 1} / {roadmap.stages.length}
                </span>
                <button
                  type="button"
                  aria-label="Next stage"
                  disabled={selected === roadmap.stages.length - 1}
                  onClick={() => setSelected((v) => Math.min(roadmap.stages.length - 1, v + 1))}
                >
                  →
                </button>
              </Stepper>
            </DetailTop>
            <DetailTitle>{stage.title}</DetailTitle>
            <Lead>{stage.body}</Lead>
          </div>
          <Items $now={selected === current}>
            {stage.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </Items>
        </Detail>
      </Container>
    </Wrap>
  );
};

export default Roadmap;
